import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Eyebrow, Reveal } from "@/components/Reveal";

function LegalPage({ title, intro, sections }) {
    return (
        <div className="bg-white text-slate-950">
            <Nav />
            <main className="mx-auto max-w-3xl px-6 pb-24 pt-40 sm:px-10 md:pt-48">
                <Reveal>
                    <Eyebrow>Legal</Eyebrow>
                </Reveal>
                <Reveal delay={0.08}>
                    <h1 className="mt-7 text-5xl font-extralight tracking-[-0.03em] text-slate-950">{title}</h1>
                </Reveal>
                <Reveal delay={0.14}>
                    <p className="mt-8 text-base font-extralight leading-relaxed text-slate-600">{intro}</p>
                </Reveal>
                <div className="mt-12 flex flex-col gap-10">
                    {sections.map((s) => (
                        <Reveal key={s.h} y={20}>
                            <section>
                                <h2 className="text-xl font-light tracking-tight text-slate-950">{s.h}</h2>
                                <p className="mt-3 text-base font-extralight leading-relaxed text-slate-600">{s.p}</p>
                            </section>
                        </Reveal>
                    ))}
                </div>
                <Reveal>
                    <Link
                        to="/"
                        className="mt-16 inline-flex items-center gap-2 text-sm font-light text-slate-500 transition-colors duration-300 hover:text-slate-950"
                        data-testid="legal-back-link"
                    >
                        <ArrowLeft size={16} strokeWidth={1.5} />
                        Back to home
                    </Link>
                </Reveal>
            </main>
            <Footer />
        </div>
    );
}

const PRIVACY_SECTIONS = [
    {
        h: "Who we are",
        p: "EDIT Technologies Limited (“EDIT”, “we”, “us”) provides a fashion discovery and shopping platform. This policy explains how we handle information collected through this landing page.",
    },
    {
        h: "What we collect",
        p: "When you join our waitlist, we collect your email address, your consent to receive updates from us, and the date and time you signed up. We do not collect anything else through this page.",
    },
    {
        h: "How we use it",
        p: "We use your email address to contact you about EDIT's launch and to share occasional updates about the product. We never sell, rent or trade your details with third parties for their own marketing.",
    },
    {
        h: "How we store it",
        p: "Waitlist signups are stored securely in our hosted database with access limited to the EDIT team. We keep your email address until you ask us to remove it.",
    },
    {
        h: "Your choices and rights",
        p: "You can unsubscribe from our emails at any time using the link in any message, and you may ask us for a copy of the information we hold about you, ask us to correct it, or ask us to delete it entirely.",
    },
    {
        h: "Contact",
        p: "For any privacy question or request, contact us at editvirtualstylist@gmail.com and we will respond as soon as we can.",
    },
];

const TERMS_SECTIONS = [
    {
        h: "Acceptance of these terms",
        p: "By using this website or joining the EDIT waitlist you agree to these terms and conditions. If you do not agree, please do not use the site.",
    },
    {
        h: "About EDIT",
        p: "EDIT is a fashion discovery and shopping platform currently in development. Features described on this site are planned functionality and may change before launch. Joining the waitlist does not guarantee access, a launch date or any particular feature set.",
    },
    {
        h: "The waitlist",
        p: "By submitting your email address you consent to hearing from us about EDIT's launch. There is no charge to join the waitlist and no purchase will be made through this website.",
    },
    {
        h: "Intellectual property",
        p: "The EDIT name, logo, website design and all content on this site belong to EDIT Technologies Limited. You may not copy or reuse them without our written permission.",
    },
    {
        h: "Acceptable use",
        p: "You agree not to misuse this website, including attempting to breach its security, submitting false information or using it in any way that causes harm to us or others.",
    },
    {
        h: "Liability",
        p: "This website is provided “as is”. To the extent permitted by law, EDIT accepts no liability for any loss arising from your use of this website or reliance on its content.",
    },
    {
        h: "Changes",
        p: "We may update these terms from time to time. Changes take effect when published on this page, and the version published here applies to your ongoing use of the site.",
    },
    {
        h: "Contact",
        p: "Questions about these terms can be sent to editvirtualstylist@gmail.com.",
    },
];

export function PrivacyPage() {
    return (
        <LegalPage
            title="Privacy Policy"
            intro="Your privacy matters to us. This policy sets out, in plain language, what EDIT collects through this website and how that information is used and protected."
            sections={PRIVACY_SECTIONS}
        />
    );
}

export function TermsPage() {
    return (
        <LegalPage
            title="Terms & Conditions"
            intro="These are the terms that apply to your use of the EDIT landing page and waitlist. They are a starting point and will be updated before full launch."
            sections={TERMS_SECTIONS}
        />
    );
}
